import { useEffect, useState } from 'react';
import { companyRequest } from '@/company/api';
import { WorkspaceIcon, type WorkspaceIconName } from '../icons';
import { useConnectionPeople } from '../connect-access/policy-fields';
import type {
  ConnectionAuditEvent,
  ConnectionAuditPage,
} from '../../../src/connectors/read-access/index.mjs';
import type { ConnectionWords } from './words';

const RECENT = 20;

/** One connection's recent reads, changes and setting changes, from the company's activity log. */
export function PanelActivity({
  connectionId,
  words,
  onOpenAll,
}: {
  connectionId: string;
  words: ConnectionWords;
  onOpenAll?: () => void;
}) {
  const { copy } = words;
  const people = useConnectionPeople();
  const [page, setPage] = useState<ConnectionAuditPage | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setFailed(false);
    const query = new URLSearchParams({ connection: connectionId, limit: String(RECENT) });
    void companyRequest<ConnectionAuditPage>(`/api/v1/company/connection-activity?${query}`)
      .then((value) => {
        if (active) setPage(value);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [connectionId, attempt]);
  if (failed)
    return (
      <div className="ws-conn-problem" role="alert">
        <span>{copy.activityLoadError}</span>
        <button type="button" className="ws-text-button" onClick={() => setAttempt((n) => n + 1)}>
          {copy.retry}
        </button>
      </div>
    );
  if (!page)
    return (
      <p className="ws-conn-note" role="status">
        {copy.activityLoading}
      </p>
    );
  if (!page.items.length) return <p className="ws-conn-note">{words.access.activityEmpty}</p>;
  const groups = groupByDay(page.items, words);
  return (
    <>
      {groups.map((group) => (
        <section key={group.label} className="ws-conn-activity-day" aria-label={group.label}>
          <h3>{group.label}</h3>
          <ol>
            {group.events.map((event) => {
              const view = describe(event, words);
              const person = people.find((entry) => entry.userId === event.userId);
              return (
                <li key={event.id} className="ws-conn-event">
                  <time dateTime={event.occurredAt}>
                    {new Intl.DateTimeFormat(words.locale, { timeStyle: 'short' }).format(
                      new Date(event.occurredAt),
                    )}
                  </time>
                  <span className="ws-conn-event-icon" data-tone={view.tone}>
                    <WorkspaceIcon name={view.icon} size={14} />
                  </span>
                  <span className="ws-conn-event-text">
                    <strong>{view.title}</strong>
                    <small>
                      {[
                        person?.displayName || person?.email || null,
                        view.result,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </small>
                  </span>
                  <span className="ws-conn-event-kind" data-tone={view.tone}>
                    {view.kind}
                  </span>
                </li>
              );
            })}
          </ol>
        </section>
      ))}
      {onOpenAll && (
        <button type="button" className="ws-text-button ws-conn-activity-all" onClick={onOpenAll}>
          {copy.activityAll}
          <WorkspaceIcon name="arrow" size={14} />
        </button>
      )}
    </>
  );
}

function describe(
  event: ConnectionAuditEvent,
  words: ConnectionWords,
): {
  title: string;
  kind: string;
  result: string | null;
  icon: WorkspaceIconName;
  tone?: 'change' | 'denied';
} {
  const { copy } = words;
  const result =
    event.connection.decision === 'denied' || event.connection.result === 'denied'
      ? copy.activityDenied
      : event.connection.result === 'failed'
        ? copy.activityFailed
        : event.connection.result === 'unknown'
          ? copy.activityUnknown
          : null;
  if (event.eventKind !== 'connection.call') {
    const titles: Record<string, string> = {
      'connection.created': copy.eventCreated,
      'connection.shared': copy.eventShared,
      'connection.user.granted': copy.eventPersonAdded,
      'connection.user.removed': copy.eventPersonRemoved,
      'connection.access.changed': copy.eventAccessChanged,
      'connection.revoked': copy.eventRevoked,
      'connection.removed': copy.eventRemoved,
      'connection.settings.changed': copy.eventSettings,
    };
    return {
      title: titles[event.eventKind] ?? event.eventKind,
      kind: copy.activitySetting,
      result: null,
      icon: 'settings',
    };
  }
  const change = event.connection.effect !== 'read';
  return {
    title: event.connection.tool ?? (change ? copy.activityChange : copy.activityRead),
    kind: change ? copy.activityChange : copy.activityRead,
    result,
    icon: result === copy.activityDenied ? 'close' : change ? 'edit' : 'eye',
    tone: result === copy.activityDenied ? 'denied' : change ? 'change' : undefined,
  };
}

function groupByDay(events: ConnectionAuditEvent[], words: ConnectionWords) {
  const day = (value: Date) => value.toDateString();
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86_400_000);
  const groups: { label: string; events: ConnectionAuditEvent[] }[] = [];
  for (const event of events) {
    const at = new Date(event.occurredAt);
    const label =
      day(at) === day(today)
        ? words.copy.activityToday
        : day(at) === day(yesterday)
          ? words.copy.activityYesterday
          : (words.date(event.occurredAt) ?? event.occurredAt);
    const last = groups.at(-1);
    if (last?.label === label) last.events.push(event);
    else groups.push({ label, events: [event] });
  }
  return groups;
}
